import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { createPaymentIntent } from "@/api/payment";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { CreditCard, Banknote, ArrowLeft, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { paymentService } from "@/services/paymentService";
import { StudentPayment } from "@/types";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

interface PaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  enrollmentId: string;
  onPaymentComplete: (status: string, method: string) => void;
  onBeforePayment?: () => Promise<void>;
}

const PaymentForm = ({ 
  amount, 
  enrollmentId, 
  onPaymentComplete,
  onBeforePayment,
  onError
}: { 
  amount: number; 
  enrollmentId: string; 
  onPaymentComplete: (status: string, method: string) => void;
  onBeforePayment?: () => Promise<void>;
  onError: (message: string) => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    try {
      // Get the card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Call onBeforePayment first to create the enrollment and get the real ID
      if (onBeforePayment) {
        await onBeforePayment();
      }

      // Get the updated enrollment ID from sessionStorage
      const pendingEnrollment = sessionStorage.getItem('pendingEnrollment');
      if (!pendingEnrollment) {
        throw new Error('No pending enrollment found');
      }

      const enrollmentData = JSON.parse(pendingEnrollment);
      const realEnrollmentId = enrollmentData.id;

      if (!realEnrollmentId) {
        throw new Error('Invalid enrollment ID');
      }

      // Create payment record first
      const paymentData: Omit<StudentPayment, 'id' | 'createdAt' | 'updatedAt'> = {
        enrollmentId: realEnrollmentId,
        studentId: enrollmentData.userId,
        studentName: enrollmentData.studentName,
        studentEmail: enrollmentData.studentEmail,
        courseId: enrollmentData.courseId,
        courseName: enrollmentData.courseName,
        amount: amount,
        paymentMethod: 'card',
        status: 'pending'
      };

      // Only add studentPhone if it exists
      if (enrollmentData.studentPhone) {
        paymentData.studentPhone = enrollmentData.studentPhone;
      }

      // Create the payment record and get its ID
      const paymentId = await paymentService.createPayment(paymentData);

      // Create payment intent with the real enrollment ID and user's email
      const { clientSecret } = await createPaymentIntent(
        amount, // Amount is already in pounds
        realEnrollmentId,
        user?.email || enrollmentData.studentEmail
      );

      if (!clientSecret) {
        throw new Error('Failed to create payment intent');
      }

      // Confirm the card payment
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: user?.email || enrollmentData.studentEmail,
            name: enrollmentData.studentName,
          },
        },
        setup_future_usage: 'off_session',
      });

      if (error) {
        console.error('Payment confirmation error:', error);
        let errorMessage = "An error occurred during payment";
        
        if (error.code === 'card_declined' && error.decline_code === 'live_mode_test_card') {
          errorMessage = "Please use a real card for payment. Test cards are not accepted in production.";
        } else if (error.message) {
          errorMessage = error.message;
        }

        // Update payment status to failed in Firestore
        try {
          await paymentService.updatePaymentStatus(paymentId, 'failed', errorMessage);
        } catch (updateError) {
          console.error('Error updating payment status:', updateError);
        }

        // Show error in dialog
        onError(errorMessage);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        // Update payment status to paid using the correct payment ID
        await paymentService.updatePaymentMethod(paymentId, 'card');
        await paymentService.updatePaymentStatus(paymentId, 'paid');
        onPaymentComplete("success", "card");
        navigate("/payment/success");
      }
    } catch (err) {
      console.error("Payment error:", err);
      onError("An unexpected error occurred during payment processing");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-3">
            <CreditCard className="h-5 w-5 text-primary" />
            <span className="font-medium">Card Payment</span>
          </div>
          <div className="text-sm text-gray-500">
            Secure payment powered by Stripe
          </div>
        </div>

        <div className="p-4 border rounded-lg bg-white">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <span className="font-medium">Total Amount</span>
          <span className="text-xl font-bold text-primary">£{amount.toFixed(2)}</span>
        </div>

        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          className="w-full h-12 text-lg font-semibold bg-primary hover:bg-primary/90"
        >
          {isProcessing ? (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Processing...
            </div>
          ) : (
            "Pay Now"
          )}
        </Button>
      </div>
    </form>
  );
};

export function PaymentDialog({ 
  isOpen, 
  onClose, 
  amount, 
  enrollmentId, 
  onPaymentComplete,
  onBeforePayment 
}: PaymentDialogProps) {
  const [paymentMethod, setPaymentMethod] = useState<"card" | "cash" | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorDialog, setErrorDialog] = useState<{ isOpen: boolean; message: string }>({ isOpen: false, message: '' });
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleCashPayment = async () => {
    setIsProcessing(true);
    try {
      // Call onBeforePayment if provided
      if (onBeforePayment) {
        await onBeforePayment();
      }

      // Get the enrollment data from sessionStorage
      const pendingEnrollment = sessionStorage.getItem('pendingEnrollment');
      if (!pendingEnrollment) {
        throw new Error('No pending enrollment found');
      }

      const enrollmentData = JSON.parse(pendingEnrollment);
      
      if (!enrollmentData.id) {
        throw new Error('Invalid enrollment data - missing ID');
      }
      
      // Create payment record for cash payment
      const cashPaymentData: Omit<StudentPayment, 'id' | 'createdAt' | 'updatedAt'> = {
        enrollmentId: enrollmentData.id,
        studentId: enrollmentData.userId,
        studentName: enrollmentData.studentName,
        studentEmail: enrollmentData.studentEmail,
        courseId: enrollmentData.courseId,
        courseName: enrollmentData.courseName,
        amount: amount,
        paymentMethod: 'cash',
        status: 'pending'
      };

      // Only add studentPhone if it exists
      if (enrollmentData.studentPhone) {
        cashPaymentData.studentPhone = enrollmentData.studentPhone;
      }

      console.log('Creating cash payment:', cashPaymentData);
      const paymentId = await paymentService.createPayment(cashPaymentData);
      console.log('Created cash payment with ID:', paymentId);
      
      onPaymentComplete("pending", "cash");
      toast({
        title: "Cash Payment Initiated",
        description: "Please complete the payment at our office to activate your enrollment.",
      });
      onClose(); // Close the dialog before navigating
      navigate("/payment/success?method=cash");
    } catch (error) {
      console.error('Error processing cash payment:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process cash payment request",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">Complete Your Payment</DialogTitle>
          </DialogHeader>
          
          {!paymentMethod ? (
            <div className="space-y-6 py-4">
              <div className="text-center space-y-2">
                <div className="text-4xl font-bold text-primary">£{amount.toFixed(2)}</div>
                <p className="text-base text-gray-600">Select your preferred payment method</p>
              </div>
              
              <div className="grid gap-4">
                <Button
                  onClick={() => setPaymentMethod("card")}
                  className={cn(
                    "h-auto p-6 flex flex-col items-center justify-center gap-3",
                    "bg-white border-2 border-primary hover:bg-primary/5 transition-colors"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-7 w-7 text-primary" />
                    <span className="font-semibold text-xl text-gray-900">Card Payment</span>
                  </div>
                  <p className="text-base text-gray-600">Pay securely online with your card</p>
                </Button>
                
                <Button
                  onClick={() => setPaymentMethod("cash")}
                  className={cn(
                    "h-auto p-6 flex flex-col items-center justify-center gap-3",
                    "bg-white border-2 border-primary hover:bg-primary/5 transition-colors"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Banknote className="h-7 w-7 text-primary" />
                    <span className="font-semibold text-xl text-gray-900">Cash Payment</span>
                  </div>
                  <p className="text-base text-gray-600">Pay at our office</p>
                </Button>
              </div>
            </div>
          ) : paymentMethod === "card" ? (
            <div className="space-y-6">
              <Button
                variant="ghost"
                onClick={() => setPaymentMethod(null)}
                className="mb-4 -ml-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to payment methods
              </Button>
              <Elements stripe={stripePromise}>
                <PaymentForm
                  amount={amount}
                  enrollmentId={enrollmentId}
                  onPaymentComplete={onPaymentComplete}
                  onBeforePayment={onBeforePayment}
                  onError={(message) => setErrorDialog({ isOpen: true, message })}
                />
              </Elements>
            </div>
          ) : (
            <div className="space-y-6">
              <Button
                variant="ghost"
                onClick={() => setPaymentMethod(null)}
                className="mb-4 -ml-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to payment methods
              </Button>
              
              <div className="text-center space-y-6">
                <div className="flex flex-col items-center gap-4">
                  <div className="p-4 bg-primary/10 rounded-full">
                    <Banknote className="h-12 w-12 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Cash Payment</h3>
                    <p className="text-gray-600">
                      Please visit our office to complete your payment
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Amount to Pay</span>
                    <span className="font-semibold">£{amount.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Payment Status</span>
                    <span className="flex items-center gap-1 text-amber-600">
                      <Clock className="h-4 w-4" />
                      Pending
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleCashPayment}
                  disabled={isProcessing}
                  className="w-full h-12 text-lg font-semibold bg-primary hover:bg-primary/90"
                >
                  {isProcessing ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </div>
                  ) : (
                    "Confirm Cash Payment"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={errorDialog.isOpen} onOpenChange={(open) => setErrorDialog(prev => ({ ...prev, isOpen: open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Payment Error
            </DialogTitle>
            <DialogDescription className="pt-4">
              {errorDialog.message}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button
              onClick={() => setErrorDialog({ isOpen: false, message: '' })}
              variant="default"
            >
              Try Again
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
} 